import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import useFinicityConnect from 'Hooks/Aggregators/useFinicityConnect';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useEventTrackingLogEvent, { categories, eventSubTypes, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { FINICITY_CONNECT_ELEMENT_ID } from '.';

const FinicityLinkContext = React.createContext(undefined);

// Actions
export const SHOW_CONNECT_VIEW = 'show-connect-view';
export const SHOW_CONNECT_VIEW_FAILED = 'show-connect-view-failed';
export const HIDE_CONNECT_VIEW = 'hide-connect-view';

const getInitialState = () => {
  return {
    showConnectView: false,
    showConnectViewError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case SHOW_CONNECT_VIEW: {
      return {
        ...state,
        showConnectView: true,
      };
    }
    case SHOW_CONNECT_VIEW_FAILED: {
      return {
        ...state,
        showConnectViewError: action.showConnectViewError,
      };
    }
    case HIDE_CONNECT_VIEW: {
      return {
        ...state,
        showConnectView: false,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const FinicityLinkProvider = ({
  resource,
  children,
  onLoadLink,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, getInitialState);
  const connectErrorCodeRef = React.useRef(null);
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const { displayErrorMessage, displaySuccess } = useSnackbarContext();

  const handleLinkUser = React.useCallback(async (event) => {
    const {
      data = {},
    } = event;

    if (data.errorCode) {
      connectErrorCodeRef.current = data.errorCode;
    }
  }, []);

  const handleLinkFailure = React.useCallback(async () => {
    logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.failure, {
      eventProperties: {
        aggregator: 'finicity',
        institution: resource.attr('institutionName'),
      },
    });
    const onFailureLink = resource.relationship(fr.relationships.onFailure.name).link('related');
    await onLoadLink(onFailureLink);
  }, []);

  const handleLinkSuccess = React.useCallback(async () => {
    try {
      await resource
        .relationship(fr.relationships.syncFinicityAccounts.name)
        .link('related')
        .Post({}, { raw: true, deprecatedAllowAlterOfRelation: true });

      await onLoadLink(resource.relationship(fr.relationships.onSuccess.name).link('related'));
      displaySuccess('Successfully synced accounts');
      logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.success, {
        eventProperties: {
          aggregator: 'finicity',
          institution: resource.attr('institutionName'),
        },
      });
    } catch (err) {
      displayErrorMessage(err, 'Failed to sync accounts');
      await handleLinkFailure();
    }
  }, []);

  const handleClose = React.useCallback(async () => {
    logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.exiting, {
      eventProperties: {
        aggregator: 'finicity',
        institution: resource.attr('institutionName'),
      },
    });
    const connectErrorCode = connectErrorCodeRef.current;

    if (connectErrorCode) {
      await handleLinkFailure();
    }
    dispatch({ type: HIDE_CONNECT_VIEW });
  }, []);

  const {
    handleClickInitiate,
    isInitialized,
    initializationError,
  } = useFinicityConnect(resource, FINICITY_CONNECT_ELEMENT_ID, {
    onSuccess: handleLinkSuccess,
    onClose: handleClose,
    onError: handleLinkFailure,
    onUser: handleLinkUser,
  });

  const handleClick = React.useCallback(async () => {
    logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.initializing, {
      eventProperties: {
        aggregator: 'finicity',
        institution: resource.attr('institutionName'),
      },
    });
    try {
      dispatch({ type: SHOW_CONNECT_VIEW });
      await handleClickInitiate();
    } catch (err) {
      dispatch({ type: SHOW_CONNECT_VIEW_FAILED, showConnectViewError: err });
    }
  }, [handleClickInitiate]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitialized,
      initializationError,
      showConnectView: state.showConnectView,
      showConnectViewError: state.showConnectViewError,
      handleClick,
    };
  }, [
    state,
    isInitialized,
    initializationError,
    handleClick,
  ]);

  return (
    <FinicityLinkContext.Provider value={memoizedValue}>
      {children}
    </FinicityLinkContext.Provider>
  );
};

// Use Survey State Context
const useFinicityLinkContext = () => {
  const context = React.useContext(FinicityLinkContext);
  if (context === undefined) {
    throw new Error('useFinicityLinkContext must be used within a FinicityLinkProvider');
  }
  return context;
};

FinicityLinkProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  resource: PropTypes.object.isRequired,
  onLoadLink: PropTypes.func.isRequired,
};

export {
  FinicityLinkProvider,
  useFinicityLinkContext,
};
